/**
 * Airport overlay — OurAirports point data + awareness circles + runway fans.
 * Large airports visible from zoom 3, medium from 5, small from 7, heliports from 5.
 * Awareness circles & runway fans are bundled here so a single "Airports" toggle
 * controls dots, circles, and fans together.
 * Click shows a dark-themed popup with airport details.
 * Large airport names are hyperlinked to their website when available.
 */
import mapboxgl from 'mapbox-gl';
import { fetchJSON } from '../utils/fetch-data';
import { addRunwayZoneLayer, removeRunwayZoneLayer } from './runway-zones';

const SOURCE_ID = 'airports';
const LAYER_LARGE    = 'airport-large';
const LAYER_MEDIUM   = 'airport-medium';
const LAYER_SMALL    = 'airport-small';
const LAYER_HELIPORT = 'airport-heliport';
const ALL_LAYERS = [LAYER_LARGE, LAYER_MEDIUM, LAYER_SMALL, LAYER_HELIPORT];

// Colors by airport type
const TYPE_COLOR: Record<string, string> = {
  large_airport:  '#ef4444',   // red
  medium_airport: '#f59e0b',   // amber
  small_airport:  '#94a3b8',   // slate
  heliport:       '#a855f7',   // purple
};

let geojsonData: GeoJSON.FeatureCollection | null = null;
let activePopup: mapboxgl.Popup | null = null;

/** Fetch and cache airport GeoJSON (lazy — only on first enable) */
async function loadAirports(): Promise<GeoJSON.FeatureCollection> {
  if (geojsonData) return geojsonData;
  geojsonData = await fetchJSON<GeoJSON.FeatureCollection>('/data/airports.json');
  return geojsonData;
}

/** Add airport source + layers + awareness circles + runway fans to the map */
export async function addAirportLayer(map: mapboxgl.Map): Promise<void> {
  const data = await loadAirports();

  // Guard: source may already exist if called twice quickly
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data });
  }

  if (!map.getLayer(LAYER_LARGE)) {
    const circleLayer = (id: string, type: string, minzoom: number, color: string, radius: [number, number]): mapboxgl.CircleLayerSpecification => ({
      id,
      type: 'circle',
      source: SOURCE_ID,
      minzoom,
      filter: ['==', ['get', 'type'], type],
      paint: {
        'circle-color': color,
        'circle-radius': ['interpolate', ['linear'], ['zoom'], minzoom, radius[0], 10, radius[1]],
        'circle-opacity': 0.85,
        'circle-stroke-width': 1,
        'circle-stroke-color': 'rgba(255,255,255,0.3)',
      },
    });

    // Separate layer per type so minzoom works correctly
    map.addLayer(circleLayer(LAYER_LARGE,    'large_airport',  3, TYPE_COLOR.large_airport,  [4, 9]));
    map.addLayer(circleLayer(LAYER_MEDIUM,   'medium_airport', 5, TYPE_COLOR.medium_airport, [3, 7]));
    map.addLayer(circleLayer(LAYER_SMALL,    'small_airport',  7, TYPE_COLOR.small_airport,  [2, 5]));
    map.addLayer(circleLayer(LAYER_HELIPORT, 'heliport',       5, TYPE_COLOR.heliport,       [2, 5]));

    // Click + hover on all circle layers (no always-on labels — info shown on click only)
    const circleLayers = [LAYER_LARGE, LAYER_MEDIUM, LAYER_SMALL, LAYER_HELIPORT];
    for (const layerId of circleLayers) {
      map.on('click', layerId, onAirportClick);
      map.on('mouseenter', layerId, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', layerId, () => { map.getCanvas().style.cursor = ''; });
    }
  }

  // Also add awareness circles + runway fans (they live in runway-zones.ts)
  await addRunwayZoneLayer(map);
}

/** Remove airport layers + source + awareness circles + runway fans from the map */
export function removeAirportLayer(map: mapboxgl.Map): void {
  activePopup?.remove();
  activePopup = null;

  for (const layerId of ALL_LAYERS) {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  }
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);

  for (const layerId of [LAYER_LARGE, LAYER_MEDIUM, LAYER_SMALL, LAYER_HELIPORT]) {
    map.off('click', layerId, onAirportClick);
  }

  // Also remove awareness circles + runway fans
  removeRunwayZoneLayer(map);
}

function onAirportClick(e: mapboxgl.MapLayerMouseEvent) {
  if (!e.features?.length) return;

  // Stop the click from bubbling to the country-fills layer
  e.originalEvent.stopPropagation();

  const props = e.features[0].properties as Record<string, string>;
  const coords = (e.features[0].geometry as GeoJSON.Point).coordinates as [number, number];

  activePopup?.remove();

  const typeLabel: Record<string, string> = {
    large_airport:  'Large Airport',
    medium_airport: 'Medium Airport',
    small_airport:  'Small Airport',
    heliport:       'Heliport',
  };

  // Name: hyperlinked for large airports with websites
  const nameHtml = (props.type === 'large_airport' && props.website)
    ? `<a href="${props.website}" target="_blank" rel="noopener">${props.name}</a>`
    : props.name;

  const iata    = props.iata    ? ` / ${props.iata}` : '';
  const elev    = props.elev    ? `<div class="ap-popup-meta">Elevation: ${props.elev} ft</div>` : '';
  const sched   = props.sched === 'yes' ? '<div class="ap-popup-meta ap-popup-sched">✈ Scheduled service</div>' : '';
  const city    = props.city    ? `<div class="ap-popup-city">${props.city}, ${props.country}</div>` : '';
  const website = props.website ? `<a href="${props.website}" target="_blank" rel="noopener" class="ap-popup-link">🌐 Official website</a>` : '';
  const wiki    = props.wiki    ? `<a href="${props.wiki}" target="_blank" rel="noopener" class="ap-popup-link">📖 Wikipedia</a>` : '';

  activePopup = new mapboxgl.Popup({ closeButton: true, maxWidth: '280px' })
    .setLngLat(coords)
    .setHTML(`
      <div class="ap-popup">
        <div class="ap-popup-name">${nameHtml}</div>
        <div class="ap-popup-code">${props.icao}${iata}</div>
        ${city}
        <div class="ap-popup-type">${typeLabel[props.type] ?? props.type}</div>
        ${elev}${sched}
        ${website || wiki ? `<div class="ap-popup-links">${website}${wiki}</div>` : ''}
      </div>
    `)
    .addTo((e.target as mapboxgl.Map));
}
